import type {
  Cmd,
  ImmutableProgram,
  InitialProperties,
  ImmutableResult,
} from '#lit/components/mixins/immutable_component/index.js';
import {
  Variant,
  makeVariants,
} from '#lit/components/mixins/immutable_component/index.js';
import type {AudienceDatasetIds} from '#lit/contexts/audience.js';
import type {HTTPResponse} from '#lit/lib/http/immutable_fetcher.js';
import type {VariantsFor} from '#lit/types/architecture.js';
import type {Audience} from '#lit/types/audiences.js';
import type {
  DestinationConnection,
  MatchFieldResponse,
} from '#types/destinations.js';
import {fold} from '#lit/types/Either.js';
import type {Nullable} from '#lit/types/general.js';
import {createDiscriminator} from '#lit/utils/discriminator.js';
import type DestinationsProvider from './index.js';
import type {Dataset} from '#lit/types/datasets.js';
import type {IngestSource, Platform} from '#types/ingest.js';
import {AudienceExportControllerActions} from '#lit/controllers/audience_export_controller/audience_export_controller.js';

export type Destinations = Map<number, DestinationConnection>;
const discriminate = createDiscriminator<
  HTTPResponse<MatchFieldResponse>,
  Model
>()('kind');

export enum PROVIDER_STATES {
  LOADING = 'loading',
  LOADED = 'loaded',
  ERROR = 'error',
}

export type Model = {
  destinations: Destinations;
  state: PROVIDER_STATES;
  errors: string[];
  audience: Nullable<AudienceDatasetIds>;
  datasets: Dataset[];
  ingests: IngestSource[];
  platforms: Platform[];
};

export const Msg = makeVariants({
  setDestinations:
    Variant<HTTPResponse<MatchFieldResponse>>()('set-destinations'),
  setAudience: Variant<Nullable<Audience>>()('set-audience'),
  setDatasets: Variant<Dataset[]>()('set-datasets'),
  setIngests: Variant<IngestSource[]>()('set-ingests'),
  setPlatforms: Variant<Platform[]>()('set-platforms'),
});

export type Msg = VariantsFor<typeof Msg>;
// -- Init

function init(props: InitialProperties): ImmutableResult<Model> {
  const audience = <Nullable<AudienceDatasetIds>>props.get('audience') ?? null;
  const datasets = <Dataset[]>props.get('datasets') ?? [];
  const ingests = <IngestSource[]>props.get('ingests') ?? [];
  const platforms = <Platform[]>props.get('platforms') ?? [];

  return {
    destinations: new Map<number, DestinationConnection>(),
    audience,
    state: PROVIDER_STATES.LOADING,
    errors: [],
    datasets,
    ingests,
    platforms,
  };
}

// -- Update

function update(
  this: DestinationsProvider,
  msg: Msg,
  model: Model,
  cmd: Cmd
): ImmutableResult<Model> {
  switch (msg.tag) {
    case 'set-destinations':
      return [
        discriminate(msg.value)({
          pending: () => ({...model, state: PROVIDER_STATES.LOADING}),
          complete: ({response}) =>
            fold(
              (err: Error) => ({
                ...model,
                errors: [...model.errors, err.message],
                state: PROVIDER_STATES.ERROR,
              }),
              (destinations: MatchFieldResponse) => ({
                ...model,
                destinations: buildContextData(destinations),
                state: PROVIDER_STATES.LOADED,
              })
            )(response),
        }),
        cmd((host: DestinationsProvider) => {
          host.destinationData.updateObservers();
        }),
      ];
    case 'set-audience':
      return {
        ...model,
        audience: msg.value,
      };
    case 'set-datasets':
      return [
        {
          ...model,
          datasets: msg.value,
        },
        cmd((host: DestinationsProvider) => {
          host.datasetsContext.setValue(msg.value);
        }),
      ];
    case 'set-ingests':
      return [
        {
          ...model,
          ingests: msg.value,
        },
        cmd((host: DestinationsProvider) => {
          host.controller.emitAction(
            AudienceExportControllerActions.SET_INGESTS,
            msg.value
          );
        }),
      ];
    case 'set-platforms':
      return [
        {
          ...model,
          platforms: msg.value,
        },
        cmd((host: DestinationsProvider) => {
          host.controller.emitAction(
            AudienceExportControllerActions.SET_PLATFORMS,
            msg.value
          );
        }),
      ];
  }
}

function buildContextData(destinations: MatchFieldResponse) {
  return new Map<number, DestinationConnection>(
    (destinations ?? []).map(d => [d.id, d])
  );
}

// -- Program

export const main: ImmutableProgram<Model, Msg> = {
  init,
  update,
};
